import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import DropDown from '../../../components/DropDown';
import { faMinus, faPlus, faCircleCheck, faCircle, faQuestion, faTags, faArrowRightArrowLeft, faTrashCan, faPaperclip, faPrint } from '@fortawesome/free-solid-svg-icons';
import { faMemoCircleInfo, faTextSize, faCardsBlank } from '@fortawesome/pro-solid-svg-icons';
import { DateFormatter } from '../../../utils/DateFormatter';
import { languages } from '../utils/contants';
import CustomToast from '../../../components/Toast';
import PropTypes from 'prop-types';
import { useAddLabelsToQuestionSetsMutation, useGetQuestionSetPropertiesMutation, useRenameQuestionSetMutation } from '../../../features/authentication/qSetApiSlice';
import Modal from '../../../components/Modal';
import PropertiesModal from '../../assignments/components/PropertiesModal';
import { useNavigate } from 'react-router';
import ActionIconButton from './ActionIconButton';
import { getSetType } from '../utils/getSetType';

const SetTile = ({
  activeTab,
  index,
  set,
  showDeletedSets,
  setSetsList,
  selectedSets,
  setSelectedSets,
  setShowRestore,
  selectedSetId,
  setSelectedSetId,
  selectedSetName,
  setSelectedSetName,
  setDateBasedUniqueKey
}) => {
  const [openSetProperties, setOpenSetProperties] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState({});
  const [openMangeLabelsModal, setOpenManageLabelsModal] = useState(false);
  const [labels, setLabels] = useState([]);
  const [openRenameSetModal, setOpenRenameSetModal] = useState(false);
  const [updatedDescription, setUpdatedDescription] = useState(false);
  const [expandStates, setExpandStates] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [isLabelsModified, setIsLabelsModified] = useState(false);
  const navigate = useNavigate();

  const [getQuestionSetProperties] = useGetQuestionSetPropertiesMutation();
  const [renameQuestionSet] = useRenameQuestionSetMutation();
  const [addLabelsToQuestionSets] = useAddLabelsToQuestionSetsMutation();

  const actionButtons = [
    {
      tab: 'questionsets',
      id: 'viewQuestions',
      tooltipText: 'Questions',
      icon: faQuestion,
      disabled: (set) => set.deleted,
      dataCount: (set) => set.questionCount,
      onClick: (set) => navigate(`${set.qSetId}/questions`)
    },
    {
      tab: 'questionsets',
      id: 'viewProperties',
      tooltipText: 'Properties',
      icon: faMemoCircleInfo,
      disabled: (set) => set.deleted || set.questionCount === 0,
      onClick: (set) => {
        handleGetProperties(set.qSetId);
        setSelectedSetId(set.qSetId);
        setSelectedSetName(set.description);
      }
    },
    {
      tab: 'resourcesets',
      id: 'viewResources',
      tooltipText: 'Resources',
      icon: faPaperclip,
      disabled: (set) => set.deleted,
      dataCount: (set) => set.questionCount,
      onClick: (set) => navigate(`${set.qSetId}`)
    },
    {
      tab: 'flashcardsets',
      id: 'viewflashcards',
      tooltipText: 'Flash Cards',
      icon: faCardsBlank,
      disabled: (set) => set.deleted,
      dataCount: (set) => set.questionCount,
      onClick: (set) => navigate(`${set.qSetId}/flashcards`)
    },
    {
      tab: 'all',
      id: 'viewLabels',
      tooltipText: 'Labels',
      icon: faTags,
      disabled: (set) => set.deleted,
      onClick: (set) => {
        setOpenManageLabelsModal(true);
        setLabels(!set.tags || set.tags.length === 0 ? [] : set.tags.split(',').map(tag => ({ name: tag, checked: false })));
        setSelectedSetId(set.qSetId);
        setSelectedSetName(set.description);
      }
    },
    {
      tab: 'all',
      id: 'renameSet',
      tooltipText: 'Rename',
      icon: faTextSize,
      disabled: (set) => set.deleted,
      onClick: (set) => {
        setOpenRenameSetModal(true);
        setUpdatedDescription(set.description);
        setSelectedSetId(set.qSetId);
        setSelectedSetName(set.description);
      }
    },
    {
      tab: 'flashcardsets',
      id: 'printflashcards',
      tooltipText: 'Print',
      icon: faPrint,
      disabled: (set) => set.deleted,
      onClick: (set) =>  navigate(`${set.qSetId}/displayset`, {  state: { setName: set.description, languageId: set.languageId } })
    },
  ];

  const toggleExpand = (qSetId) => {
    if (expandStates.includes(qSetId))
      setExpandStates((prev) => prev.filter((id) => id !== qSetId));
    else
      setExpandStates((prev) => [...prev, qSetId]);
  };

  const handleLanguageChanges = (languageId, qSetId) => {
    setSetsList((prevQSets) =>
      prevQSets.map((qSet) =>
        qSet.qSetId === qSetId ? { ...qSet, languageId: languageId } : qSet
      )
    );
  };

  const handleGetProperties = (qSetId) => {
    setOpenSetProperties(true);
    getPropertiesAsync(qSetId);
  };

  const getPropertiesAsync = async(qSetId) => {
    if (isLoading)
      return;

    setIsLoading(true);
    try {
      const options = {
        id: qSetId
      };
      const response = await getQuestionSetProperties(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else
        setProperties(response.data);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const handleQSetSelection = (qSetId, qSetName, isDeleted, isRestorable) => {
    let selectedQSetCount = selectedSets.length;

    if(showDeletedSets) {
      if (selectedQSetCount > 0) {
        let isAlreadySelectedQSetDeleted = selectedSets.filter((qSet)=>qSet.isDeleted).length > 0;
        if (isAlreadySelectedQSetDeleted && !isDeleted) {
          CustomToast(`The selected ${getSetType(activeTab).toLowerCase()} set is not a deleted set, and does not match with already selected ${getSetType(activeTab).toLowerCase()} sets!`, 'warning');
          return;
        }
        else if (!isAlreadySelectedQSetDeleted && isDeleted) {
          CustomToast(`The selected ${getSetType(activeTab).toLowerCase()} set a deleted set, and does not match with already selected ${getSetType(activeTab).toLowerCase()} sets!`, 'warning');
          return;
        }
      }
      else {
        if(isDeleted)
          setShowRestore(true);
        else 
          setShowRestore(false);
      }
    }
    
    setSelectedSets((prev) => {
      if (prev.some((qSet) => qSet.id === qSetId)) {
        return prev.filter((qSet) => qSet.id !== qSetId);
      } else {
        return [...prev, { id: qSetId, name: qSetName, isDeleted: isDeleted, isRestorable: isRestorable }];
      }
    });

    setDateBasedUniqueKey(new Date().getTime().toString());
  };

  const handleRenameQSetsAsync = async() => {
    if(isLoading)
      return;
    
    setIsLoading(true);
    try{
      let options = {
        id: selectedSetId,
        description: updatedDescription
      };

      let response = await renameQuestionSet(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        CustomToast(`${getSetType(activeTab)} set renamed successfully.`, 'success');
        setSelectedSetId(null);
        setSelectedSetName('');
        setSetsList((prevSets) => prevSets.map((set) =>
          set.qSetId === selectedSetId
            ? { ...set, description: updatedDescription }
            : set
        ));
      }
    } catch (error) {
      console.log(error);
    }
    setOpenRenameSetModal(false);
    setIsLoading(false);
  };
  
  const addLabel = () => {
    if(labels.some((label) => label.name === newLabel)) {
      CustomToast('Label with same name already exist in the set.', 'warning');
      return;
    }
  
    setLabels((prevLabels) => [
      ...prevLabels,
      { name: newLabel, checked: false }
    ]);
    setNewLabel('');
  };
  
  const deleteLabels = () => {
    if(labels.filter((label) => label.checked).length === 0) {
      return;
    }
  
    setLabels(labels.filter((label) => !label.checked));
  };
  
  const handleUpdateLabelsAsync = async() => {
    if(isLoading)
      return;
    
    setIsLoading(true);
    try{
      let options = {
        id: selectedSetId,
        labels: labels.map((label) => label.name)
      };

      let response = await addLabelsToQuestionSets(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        setSetsList((prevSets) => prevSets.map((set) =>
          set.qSetId === selectedSetId
            ? { ...set, tags: labels.map((label) => label.name).join(',') }
            : set
        ));
        CustomToast(`${getSetType(activeTab)} set labels updated successfully.`, 'success');
        setOpenManageLabelsModal(false);
        setSelectedSetId(null);
        setSelectedSetName('');
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  return (
    <>
      <div key={index} className="questionSet-box mb-2 m-1 col-4">
        <div id={activeTab === 'questionsets' ? 'questionSetTitleBox' : 'resourceSetTitleBox'} className='title d-flex p-1 gap-3'>
          <div className='w-100 cursor-none text-break'>{set.description}</div>
          {activeTab == 'questionsets' && set.questionCount > 0 &&
            <div id={`print-${index}`} className="col d-flex gap-1 toggle-button">
              <div className='align-items-center cursor-pointer d-flex ms-auto gap-1 no-select' onClick={() => toggleExpand(set.qSetId)}>
                Display/Print
                <FontAwesomeIcon icon={expandStates.includes(set.qSetId) ? faMinus : faPlus} />
              </div>
            </div>
          }
        </div>
        <div className='mt-2 p-0'>
          {set.lastTransferDate ?
            <>
              <FontAwesomeIcon className='fs-7 mx-1 text-success' icon={faArrowRightArrowLeft} />
              <span className='fs-7 text-success'>Transferred from {set.previousOwnerName} ({set.previousOwnerLogin}) on {DateFormatter.toShortDate2(set.lastTransferDate, true)}</span>
            </> : <span className='fs-7'>&nbsp;</span>
          }
          <div className='align-items-center d-flex pb-1 h-33 mt-3'>
            {expandStates.includes(set.qSetId) ?
              <div className="gap-2 align-items-center d-flex questionSet-dropDown">
                <div className={`${activeTab === 'flashcardsets' ? 'disable-Pointer-Events' : ''} align-items-center d-flex gap-2`}>
                  <DropDown
                    label="Language"
                    value={languages}
                    selectedValue={set.languageId}
                    description={true}
                    onChangeHandler={(e) => handleLanguageChanges(Number(e.target.value), set.qSetId)}
                  />
                </div>
                <OverlayTrigger
                  key={`displaySet-${index}`}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${index}`} className='custom-tooltip'>
                      <p className='mb-0'>Display {getSetType(activeTab)} Set</p>
                    </Tooltip>
                  }>
                  <div>
                    <button className="btn btn-castleWhite" onClick={() => navigate(`${set.qSetId}/displayset`, {  state: { setName: set.description, languageId: set.languageId } })}>
                      Display Set
                    </button>
                  </div>
                </OverlayTrigger>
              </div>
              :
              <>
                <div className='d-flex justify-content-center align-items-center gap-3 justify-content-lg-start'>
                  {actionButtons.filter((action) => action.tab === 'all' || action.tab === activeTab)
                    .map((action, index) => (
                      <ActionIconButton
                        key={action.id + index}
                        id={action.id + index}
                        keyValue={action.id}
                        tooltipText={action.tooltipText}
                        icon={action.icon}
                        disabled={action.disabled(set)}
                        dataCount={action.dataCount ? action.dataCount(set) : undefined}
                        onClick={() => action.onClick(set)}
                      />
                    ))}
                </div>
                <div className={`fs-2 ms-auto questionSet-circular-check cursor-pointer ${selectedSets.some((qSet) => qSet.id === set.qSetId) ? 'check' : 'uncheck'}`} onClick={() => handleQSetSelection(set.qSetId, set.description, set.deleted, set.isRestorable)}>
                  <FontAwesomeIcon icon={selectedSets.some((qSet) => qSet.id === set.qSetId) ? faCircleCheck : faCircle} />
                </div>
              </>
            }
          </div>
        </div>
      </div>
    
      <PropertiesModal
        title={selectedSetName}
        properties={properties}
        isLoading={isLoading}
        openProperties={openSetProperties}
        setOpenProperties={()=>{setOpenSetProperties(); setSelectedSetId(null); setSelectedSetName('');}}
        qSetId={selectedSetId}
        isSetPreview={true}
      />

      <Modal
        contentWidth="600px"
        isOpen={openRenameSetModal}
        titleName={`Rename Set <span>(${selectedSetName})</span>`}
        onClose={() => setOpenRenameSetModal(false)}>
        <div>
          <label className='mb-2'>New Name</label>
          <input
            className="form-control"
            type="text"
            value={updatedDescription}
            onChange={(e) => setUpdatedDescription(e.target.value)}
          />
        </div>
        {updatedDescription.length > 50 &&
          <div className="text-danger">Set Name has exceeded a limit of 50 characters!</div>
        }
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => setOpenRenameSetModal(false)}>
            Cancel
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={isLoading || updatedDescription.length > 50 || updatedDescription.length == 0}
            onClick={() => handleRenameQSetsAsync()} 
          >
            Save
          </button>
        </div>
      </Modal>
    
      <Modal
        contentWidth="600px"
        isOpen={openMangeLabelsModal}
        titleName={`Manage Labels <span>(${selectedSetName})</span>`}
        onClose={() => {setOpenManageLabelsModal(false); setIsLabelsModified(false);}}>
        <label className='mb-2'>New Label</label>
        <div className='align-items-center d-flex gap-3'>
          <input
            className="form-control"
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <button className="btn btn-castleWhite btn-width" disabled={!newLabel.trim().length > 0}
            onClick={() => {addLabel(); setIsLabelsModified(true);}}
          >
            Add Label
          </button>
        </div>
        <div className='border mt-4 p-2 rounded-3 position-relative'>
          <p className="position-absolute top-0 start-0 translate-middle-y bg-white px-2 ms-3">
            Labels
          </p>
          {labels.length == 0 ?
            <>
              <div className="text-danger m-3">No labels have been set. Start adding labels above.</div>
            </>:<>
              <Row className='d-flex col-12 p-3'>
                {labels?.map((label, index) => (
                  <Col key={index} className="col-6">
                    <input
                      type="checkbox"
                      className="form-check-input me-2 cursor-pointer border-secondary"
                      checked={label.checked}
                      id={`label-${index}`}
                      name="labels"
                      onChange={() => {
                        setLabels((prevLabels) => 
                          prevLabels.map((lbl, idx) =>
                            lbl.name === label.name && idx == index ? { ...label, checked: !lbl.checked } : lbl
                          )
                        );
                      }}
                    />
                    <label htmlFor={`displayLabelCheck-${index}`} className="form-check-label">
                      {label.name}
                    </label>
                  </Col>
                ))}
              </Row>
              <div className='d-flex justify-content-end p-2'>
                <button
                  onClick={() => {deleteLabels(); setIsLabelsModified(true);}}
                  disabled={!labels.some(label => label.checked)}
                  className="border-0 link-castleOrange text-orange">
                  <FontAwesomeIcon icon={faTrashCan} /> Delete Checked Labels
                </button>
              </div>
            </>
          }
        </div>
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => {setOpenManageLabelsModal(false); setIsLabelsModified(false);}}>
                Cancel
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={!isLabelsModified || isLoading}
            onClick={() => {handleUpdateLabelsAsync(); setIsLabelsModified(false);}}
          >
                Save
          </button>
        </div>
      </Modal>
    </>
  );
};

SetTile.propTypes = {
  activeTab: PropTypes.string,
  index: PropTypes.number,
  set: PropTypes.object,
  showDeletedSets: PropTypes.bool,
  setSetsList: PropTypes.func,
  selectedSets: PropTypes.array,
  setSelectedSets: PropTypes.func,
  setShowRestore: PropTypes.func,
  selectedSetId: PropTypes.number,
  setSelectedSetId: PropTypes.func,
  selectedSetName: PropTypes.string,
  setSelectedSetName: PropTypes.func,
  setDateBasedUniqueKey: PropTypes.func
};

export default SetTile;
